import React, { useCallback, useEffect } from 'react';

import invariant from 'tiny-invariant';

import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { useInViewport } from '../../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../../common/utils/use-nav-analytics';
import { useScrollDirection } from '../../../common/utils/use-scroll-direction';
import type { ScrollDirection } from '../../../common/utils/use-scroll-direction/types';
import { useOptions } from '../../../controllers/options-provider';
import { useUserSideNavDataStore } from '../../../controllers/user-state/user-side-nav-data-store-provider';
import {
	SideNavVisibilityProvider,
	useSideNavVisibility,
} from '../../../controllers/visibility/use-nav-visibility';
import { SideNavigationLayout } from '../index';
import type { GSNLayoutProps } from '../types';

/**
 * GSN SideNavigationLayout - A layout component that implements the base side navigation structure.
 *
 * It utilizes the GSN context to support different functionalities such as Drag and Drop,
 * analytics, user state persistence, and side navigation visibility management.
 *
 */
export function GSNSideNavigationLayout(props: GSNLayoutProps): React.JSX.Element {
	const { setUserSideNavData } = useUserSideNavDataStore();
	const { onExpand, onCollapse, onPeekStart, onPeekEnd, onResizeStart, onResizeEnd } = props;

	return (
		<SideNavVisibilityProvider
			setUserSideNavData={setUserSideNavData}
			onExpand={onExpand}
			onCollapse={onCollapse}
			onPeekStart={onPeekStart}
			onPeekEnd={onPeekEnd}
			onResizeStart={onResizeStart}
			onResizeEnd={onResizeEnd}
		>
			<GSNLayoutComponent {...props} />
		</SideNavVisibilityProvider>
	);
}

function GSNLayoutComponent({
	preMainElem,
	postMainElem,
	bottomElem,
	children,
	testId,
	sideNavContentRef,
}: GSNLayoutProps) {
	const { fireEvent } = useAnalyticsEvents();
	const { userSideNavData } = useUserSideNavDataStore();
	const { onCollapse, onExpand, onPeekStart, onPeekEnd, onResizeStart, onResizeEnd } =
		useSideNavVisibility();
	const internalSideNavRef = React.useRef<HTMLDivElement | null>(null);
	const combinedRef = useCallback(
		(instance: HTMLDivElement | null) => {
			internalSideNavRef.current = instance;
			if (typeof sideNavContentRef === 'function') {
				sideNavContentRef(instance);
			} else if (sideNavContentRef) {
				(sideNavContentRef as React.MutableRefObject<HTMLDivElement | null>).current = instance;
			}
		},
		[sideNavContentRef],
	);
	const { l1DragAndDropEnabled } = useOptions();
	const { fireViewedSideNavEvent } = useNavAnalytics();

	const fireScrollAnalytics = useCallback(
		(direction: ScrollDirection) => {
			fireEvent('ui.sideNav.scrolled.sideNavMenu', { scrollDirection: direction });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fireEvent],
	);

	useScrollDirection(internalSideNavRef, { onScroll: fireScrollAnalytics });

	useInViewport({
		ref: internalSideNavRef,
		rootMargin: '0px',
		onInViewport: fireViewedSideNavEvent,
	});

	useEffect(() => {
		if (!l1DragAndDropEnabled) {
			return;
		}

		const scrollable = internalSideNavRef.current;
		invariant(scrollable, 'sideNavContentRef must be set');

		return autoScrollForElements({
			element: scrollable,
			canScroll: ({ source }) => source.data.type === 'menu-item',
		});
	}, [l1DragAndDropEnabled]);

	return (
		<SideNavigationLayout
			testId={testId}
			onExpand={onExpand}
			onCollapse={onCollapse}
			onPeekStart={onPeekStart}
			onPeekEnd={onPeekEnd}
			defaultWidth={userSideNavData.defaultWidth}
			defaultCollapsed={userSideNavData.defaultCollapsed}
			sideNavContentRef={combinedRef}
			preMainElem={preMainElem}
			postMainElem={postMainElem}
			onResizeEnd={onResizeEnd}
			onResizeStart={onResizeStart}
			bottomElem={bottomElem}
		>
			{children}
		</SideNavigationLayout>
	);
}
